import React from 'react';
import shortid from 'shortid';
import update from 'immutability-helper';
import classNames from 'classnames';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

// ユーティリティ関数
// 配列内の要素を別の位置に移動する関数（ドラッグ&ドロップの並び替えで使用）
const arrayMoveMutate = (array, from, to) => {
  const startIndex = to < 0 ? array.length + to : to;
  const item = array.splice(from, 1)[0];
  array.splice(startIndex, 0, item);
};

// 新しい配列を作成して要素を移動する関数（元の配列を変更せず新しい配列を返す）
const arrayMove = (array, from, to) => {
  array = array.slice();
  arrayMoveMutate(array, from, to);
  return array;
};

// 新規項目のテンプレートを返す関数
const getItem = () => {
  return {
    id: shortid.generate(),
    status: '',
    name: '新規項目',
    parentUrl: '', //テスト
    lists: [],
    image: '',
    imageAlt: '',
    imageUrl: ''
  }
}

// ドラッグ&ドロップのコンポーネント群
// ドラッグハンドルのコンポーネント（並び替えのためのアイコン）
const DragHandle = SortableHandle(() => <i className="acms-admin-icon acms-admin-icon-sort" />);

// タブ項目のコンポーネント（各タブを表示）
const SortableItem = SortableElement(({ name, number, setTab, tab }) => {
  return (<li onClick={() => {
    setTab(number);
  }}><a href="#" className={classNames({
    "acms-admin-tab-active": tab === number
  })}>
      <DragHandle />
      {name}
    </a>
  </li>)
});

// タブリストのコンポーネント（タブ全体を管理）
const SortableList = SortableContainer(({ items, setTab, tab }) => {
  return (<ul className="acms-admin-tabs-inner" style={{ marginTop: '10px' }}>
    {items.map(({ name, id }, index) => (
      <SortableItem key={id} index={index} tab={tab} number={index} name={name} setTab={setTab} />
    ))}
  </ul>)
});

// リンク項目のコンポーネント（各リンクの編集フォーム）
// ステータス（公開/非公開）、ラベル、URL、削除機能を持つ行
const SortableLinkItem = SortableElement(({
  onStatusChange,
  onLabelChange,
  onUrlChange,
  onRemove,
  label,
  status,
  url,
  number
}) => (<tr style={{ borderBottom: '1px solid #999' }}>
  <td>
    <DragHandle />
  </td>
  <td>
    <div className="acms-admin-form-checkbox">
      <label>
        <input type="checkbox"
               value="on"
               defaultChecked={status}
               onChange={(e) => {
                 onStatusChange(number, e.target.checked)
               }} />
        <i className="acms-admin-ico-checkbox"></i> 公開
      </label>
    </div>
  </td>
  <td>
    <div class="acms-admin-form-action">
      <label className="acms-admin-form-side" style={{ whiteSpace: 'nowrap' }}>ラベル</label>
      <input type="text" className="acms-admin-form-width-full" value={label} onChange={(e) => {
        onLabelChange(number, e.target.value)
      }} />
    </div>
  </td>
  <td>
    <div class="acms-admin-form-action">
      <label className="acms-admin-form-side" style={{ whiteSpace: 'nowrap' }}>URL</label>
      <input type="text" className="acms-admin-form-width-full" value={url} onChange={(e) => {
        onUrlChange(number, e.target.value);
      }} />
    </div>
  </td>
  <td style={{ textAlign: 'right' }}>
    <button
      type="button"
      className="acms-admin-btn acms-admin-btn-danger"
      onClick={() => {
        if (!confirm('項目を削除します。よろしいでしょうか？')) {
          return;
        }
        onRemove(number);
      }}>削除</button>
  </td>
</tr>));

// リンクリストのコンポーネント（セクション内のリンク一覧を管理）
const SortableLinkList = SortableContainer(({ items, onLabelChange, onUrlChange, onRemove, onStatusChange }) => (<table style={{ marginTop: '5px' }}>
  {items.map((link, index) => {
    return (<SortableLinkItem
      key={`index-${index}`}
      index={index}
      number={index}
      label={link.label}
      url={link.url}
      status={link.status}
      onRemove={onRemove}
      onStatusChange={onStatusChange}
      onLabelChange={onLabelChange}
      onUrlChange={onUrlChange}
    />);
  })}
</table>));

// メインのコンポーネント
// ナビゲーション設定用の管理画面UIを提供
// - タブの追加/削除/並び替え
// - 各タブの設定（公開設定、ラベル、URL、画像関連）
// - セクションの追加/削除
// - セクション内のリンク項目の管理
// - 最終的な設定をJSONとしてhidden inputに保存
export default ({ initialItems }) => {
  // 状態管理
  // items: すべてのナビゲーション項目を管理
  const [items, setItems] = React.useState(initialItems ? initialItems : [getItem()]);
  // tab: 現在選択中のタブを管理
  const [tab, setTab] = React.useState(0);
  const item = items[tab];

  return (<div className="acms-admin-form">
    <button type="button" className="acms-admin-btn" onClick={() => {
      setItems([...items, getItem()]);
    }}>タブを追加</button>

    <div className="acms-admin-tabs" style={{ marginTop: '10px' }}>
      <SortableList
        items={items}
        setTab={setTab}
        tab={tab}
        axis="x"
        useDragHandle={true}
        onSortEnd={({ oldIndex, newIndex }) => {
          setItems(arrayMove(items, oldIndex, newIndex));
        }}
      />
      {items.map((item, i) => {
        if (i !== tab) {
          return null;
        }
        return (<div className="acms-admin-tabs-panel" key={item.id}>
          <table className="adminTable acms-admin-table-admin-edit">
            <tr style={{ display: 'none' }}>
              <th style={{ width: '120px' }}>id</th>
              <td>
                <input type="text" className="acms-admin-form-width-full" value={item.id} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      id: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>削除</th>
              <td>
                <button
                  type="button"
                  className="acms-admin-btn acms-admin-btn-danger"
                  onClick={() => {
                    if (!confirm("タブを削除してもよろしいですか？")) {
                      return;
                    }
                    setItems(update(items, {
                      $splice: [[i, 1]]
                    }))
                  }}
                >タブを削除</button>
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>公開設定</th>
              <td>
                <div className="acms-admin-form-checkbox">
                  <input type="hidden" name="nav_status" value={item.status} />
                  <input
                    type="checkbox"
                    value="closed"
                    id="input-checkbox-status-close"
                    checked={item.status === "closed"}
                    onChange={(e) => {
                      setItems(update(items, {
                        [i]: {
                          status: {
                            $set: e.target.checked ? "closed" : ""
                          }
                        }
                      }));
                    }}
                  />
                  <label htmlFor="input-checkbox-status-close"><i className="acms-admin-ico-checkbox"></i>非公開にする</label>
                </div>
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>ラベル</th>
              <td>
                <input type="text" className="acms-admin-form-width-full" value={item.name} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      name: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>URL</th>
              <td>
                <input type="text" className="acms-admin-form-width-full" value={item.parentUrl} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      parentUrl: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>
                画像URL
              </th>
              <td class="js-media-field">
                <input type="text" className="acms-admin-form-width-full" value={item.image} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      image: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
                <div><small><a href="/bid/1/admin/media_index/" target="_blank">→ メディアからURLを取得する</a></small></div>
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>
                代替テキスト
              </th>
              <td>
                <input type="text" className="acms-admin-form-width-full" value={item.imageAlt} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      imageAlt: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>
                画像リンク
              </th>
              <td>
                <input type="text" className="acms-admin-form-width-full" value={item.imageUrl} onChange={(e) => {
                  setItems(update(items, {
                    [i]: {
                      imageUrl: {
                        $set: e.target.value
                      }
                    }
                  }));
                }} />
              </td>
            </tr>
            <tr>
              <th style={{ width: '120px' }}>セクション</th>
              <td>
                <button type="button" className="acms-admin-btn" onClick={() => {
                  setItems(update(items, {
                    [i]: {
                      lists: {
                        $push: [{
                          title: '',
                          titleUrl: '',
                          links: [{
                            label: '',
                            url: ''
                          }]
                        }]
                      }
                    }
                  }))
                }}>セクションを追加</button>
                {item.lists.map((list, j) => {
                  return (<div style={{ backgroundColor: '#eee', marginTop: '10px', padding: '10px' }}>
                    <table style={{ marginTop: '5px', borderBottom: '1px solid #666' }}>
                      <tr>
                        <td>
                          <div class="acms-admin-form-action">
                            <label className="acms-admin-form-side" style={{ whiteSpace: 'nowrap' }}>タイトル</label>
                            <input type="text" className="acms-admin-form-width-full" value={list.title} onChange={(e) => {
                              setItems(update(items, {
                                [i]: {
                                  lists: {
                                    [j]: {
                                      title: {
                                        $set: e.target.value
                                      }
                                    }
                                  }
                                }
                              }));
                            }} />
                          </div>
                        </td>
                        <td>
                          <div class="acms-admin-form-action">
                            <label className="acms-admin-form-side" style={{ whiteSpace: 'nowrap' }}>URL</label>
                            <input type="text" className="acms-admin-form-width-full" value={list.titleUrl} onChange={(e) => {
                              setItems(update(items, {
                                [i]: {
                                  lists: {
                                    [j]: {
                                      titleUrl: {
                                        $set: e.target.value
                                      }
                                    }
                                  }
                                }
                              }));
                            }} />
                          </div>
                        </td>
                        <td>
                          <div style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="acms-admin-btn acms-admin-btn-danger"
                              onClick={() => {
                                if (!confirm("セクションを削除してもよろしいですか？")) {
                                  return;
                                }
                                setItems(update(items, {
                                  [i]: {
                                    lists: {
                                      $splice: [[j, 1]]
                                    }
                                  }
                                }))
                              }}
                            >削除</button>
                          </div>
                        </td>
                      </tr>
                    </table>
                    {list.links && <>
                      <SortableLinkList
                        axis="y"
                        useDragHandle={true}
                        items={list.links}
                        onSortEnd={({ oldIndex, newIndex }) => {
                          setItems(update(items, {
                            [i]: {
                              lists: {
                                [j]: {
                                  links: {
                                    $set: arrayMove(items[i].lists[j].links, oldIndex, newIndex)
                                  }
                                }
                              }
                            }
                          }));
                        }}
                        onStatusChange={(index, value) => {
                          setItems(update(items, {
                            [i]: {
                              lists: {
                                [j]: {
                                  links: {
                                    [index]: {
                                      status: {
                                        $set: value
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }))
                        }}
                        onLabelChange={(index, value) => {
                          setItems(update(items, {
                            [i]: {
                              lists: {
                                [j]: {
                                  links: {
                                    [index]: {
                                      label: {
                                        $set: value
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }))
                        }}
                        onUrlChange={(index, value) => {
                          setItems(update(items, {
                            [i]: {
                              lists: {
                                [j]: {
                                  links: {
                                    [index]: {
                                      url: {
                                        $set: value
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }))
                        }}
                        onRemove={(index) => {
                          setItems(update(items, {
                            [i]: {
                              lists: {
                                [j]: {
                                  links: {
                                    $splice: [[index, 1]]
                                  }
                                }
                              }
                            }
                          }))
                        }}
                      />
                      <table>
                      <tr>
                        <td colSpan="2">
                          <button type="button" className="acms-admin-btn" onClick={() => {
                            setItems(update(items, {
                              [i]: {
                                lists: {
                                  [j]: {
                                    links: {
                                      $push: [{
                                        label: '',
                                        url: ''
                                      }]
                                    }
                                  }
                                }
                              }
                            }))
                          }}>追加</button>
                        </td>
                      </tr>
                      </table>
                    </>}
                  </div>)
                })}
              </td>
            </tr>
          </table>
        </div>);
      })}
    </div>
    <input type="hidden" name="config[]" value="sub_navigation_config" />
    <input type="hidden" name="sub_navigation_config" value={JSON.stringify(items)} />
  </div>);
}
